import React from 'react'
import { Box } from 'components/layout/box'
import { BlockIcon } from 'components/icons/block-icon'
import { Text } from 'components/typography/text'
import { SearchItem, PageType } from '../types'
import * as styles from '../Helix.css'

interface ResultItemProps {
	item: SearchItem
	index: number
	isSelected: boolean
	isFocused: boolean
	onClick: () => void
	onKeyDown: (e: React.KeyboardEvent) => void
	innerRef?: React.Ref<HTMLDivElement>
	virtualStyles?: React.CSSProperties
	searchTerm?: string
}

export const ResultItem: React.FC<ResultItemProps> = React.memo(
	({ item, index, isSelected, isFocused, onClick, onKeyDown, innerRef, virtualStyles }) => {
		// Parse the label to extract company, ticker, and page type
		const parseLabel = (label: string) => {
			// Match pattern: "Company (TICKER) - PageType" or "Company (TICKER)"
			const match = label.match(/^(.*?)\s*\(([A-Z0-9]+)\)(?:\s*-\s*(.*))?$/)
			if (match) {
				const [, company, ticker, pageType] = match
				return { company, ticker, pageType }
			}
			return { company: label, ticker: '', pageType: '' }
		}

		const { company, ticker, pageType } = parseLabel(item.label)

		// Format the type display based on page_type
		const formatResultType = (type: PageType) => {
			if (type === 'etf') return 'ETF'
			if (type === 'stock') return 'Stock'
			if (type === 'bot') return 'Bot'
			if (type === 'suggestion') return 'Trending'
			return 'Feature'
		}

		return (
			<div
				key={item.id}
				data-index={index}
				ref={innerRef}
				className={styles.resultItem({ selected: isSelected, isGroupHeader: item.isGroupHeader })}
				onClick={onClick}
				onKeyDown={onKeyDown}
				tabIndex={isSelected ? 0 : -1}
				role="option"
				aria-selected={isSelected}
				aria-expanded={(item.type === 'stock' || item.type === 'etf') && isFocused}
				style={virtualStyles}>
				{item.type === 'stock' || item.type === 'etf' || item.type === 'bot' ? (
					item.image ? (
						<img src={`data:image/png;base64,${item.image}`} alt={item.label} className={styles.resultImage} />
					) : (
						<div className={styles.noLogo}>
							<Text color="neutral.100" fontSize="small">
								$
							</Text>
						</div>
					)
				) : item.icon ? (
					<div className={styles.resultIcon}>
						<BlockIcon icon={item.icon as any} size="medium" color="neutral.300" />
					</div>
				) : null}
				<div className={styles.resultContent}>
					<div className={styles.resultInfo}>
						<div className={styles.resultLabel}>
							{ticker && (
								<>
									<Text variant="body1Bold" color="neutral.100">
										{ticker}
									</Text>
								</>
							)}
							{company && (
								<Box paddingLeft={2}>
									<Text variant="body1" color="neutral.100" style={{ fontSize: '14px' }}>
										{company}
									</Text>
								</Box>
							)}
							{pageType && (
								<Box paddingLeft={3}>
									<Text variant="metadata" color="neutral.300">
										{item.type}
									</Text>
								</Box>
							)}
						</div>
						{!item.isGroupHeader && <div className={styles.resultType}>{formatResultType(item.type)}</div>}
					</div>
				</div>
			</div>
		)
	}
)

ResultItem.displayName = 'ResultItem'
